import React, { useEffect, useState } from "react";
import {
  Grid,
  Container,
  Card,
  CardContent,
  Button,
  FormControl,
  FormLabel,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { getAccessToken, useAuth } from "../../context/auth";
import { GetAddressList } from "../../services/api/address-service";
import Header, { HeaderType } from "../../components/Header";
import type { AddressResponse } from "../../services/model/addressResponse";
import MapIcon from "@mui/icons-material/Map";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";

interface AddressListProps {
  style?: any;
  loading: boolean;
}

const AddressList: React.FC<AddressListProps> = (_props) => {
  const inputLabel = React.useRef<HTMLLabelElement>(null);
  const [, setLabelWidth] = React.useState(0);
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const confirm = useConfirm();
  const [addresses, setAddresses] = useState<AddressResponse[]>([]);
  let [loading, setLoading] = useState(false);

  const ViewAddress = (addressId: string) => {
    localStorage.setItem("viewAddressId", JSON.stringify(addressId));
    navigate(`/${tenant}/address`);
  };

  const goToAddressAdd = () => {
    window.localStorage.removeItem("viewAddressId");
    navigate(`/${tenant}/addeditaddress`);
  };

  const showDialog = (title: string, message: string) => {
    confirm({
      catchOnCancel: false,
      title: title,
      description: message,
    });
  };

  const fetchAddress = async () => {
    setLoading(true);
    try {
      const responseList = await GetAddressList(token);
      if (responseList) {
        const response = responseList.data as any as AddressResponse[];
        if (response) {
          const arraySize: number = response.length;
          let filledArray = new Array<AddressResponse>(arraySize);

          for (let i = 0; i < response.length; i++) {
            const id = response[i]["id"] as string;
            const googleAddress = response[i]["googleAddress"] as string;
            const latitude = response[i]["latitude"] as number;
            const longitude = response[i]["longitude"] as number;

            const selectOption: AddressResponse = {
              id: id,
              googleAddress: googleAddress,
              latitude: latitude,
              longitude: longitude,
            };

            filledArray.push(selectOption);
          }
          setAddresses(filledArray);
          setLoading(false);
        }
      } else {
        setLoading(false);
        showDialog(
          "Failure",
          "There seems to be a problem with your internet connection.\nPlease check your internet connection."
        );
      }
    } catch (err) {
      setLoading(false);
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) {
        console.error(err.message);
      }
    }
  };

  useEffect(() => {
    fetchAddress();
    setLabelWidth(inputLabel.current!.offsetWidth);
  }, []);

  let NoRecord;
  if (addresses.length <= 0) {
    NoRecord = (
      <div>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          direction="column"
        >
          <MapIcon style={{ fontSize: 70 }} />
          <br />
          <Typography variant="subtitle2">
            There are no addresses to display,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;please add a new address.
          </Typography>
        </Grid>
      </div>
    );
  } else {
    NoRecord = <></>;
  }

  return (
    <div>
      <Header type={HeaderType.AddressList} />
      <Container maxWidth="sm">
        <Grid container spacing={1}>
          <Grid size={12}>
            <Card variant="outlined" style={{ padding: "15px" }}>
              <Grid
                container
                direction="row"
                justifyContent="center"
                spacing={1}
              >
                <Grid size={12}>
                  <br />
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    direction="column"
                  >
                    <Button
                      value="addNew"
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      onClick={() => {
                        goToAddressAdd();
                      }}
                    >
                      <strong>Add New Address</strong>
                    </Button>
                  </Grid>
                  <br />
                  <Grid size={12}>
                    <FormControl variant="outlined" style={{ width: "100%" }}>
                      <FormLabel
                        ref={inputLabel}
                        htmlFor="demo-simple-select-outlined-label"
                      ></FormLabel>
                    </FormControl>
                  </Grid>
                  <Grid size={12}>
                    {addresses.map((address) => (
                      <Grid key={address["id"]} size={12}>
                        <Card
                          variant="outlined"
                          style={{ padding: "15px", background: "lightgrey" }}
                          onClick={() => ViewAddress(address.id)}
                        >
                          <Grid
                            container
                            justifyContent="center"
                            alignItems="center"
                            direction="column"
                          >
                            <Typography variant="subtitle1">
                              <strong> {address["googleAddress"]}</strong>
                            </Typography>
                          </Grid>
                        </Card>
                        <Card
                          variant="outlined"
                          style={{ padding: "15px" }}
                          onClick={() => ViewAddress(address.id)}
                        >
                          <CardContent>
                            <Typography variant="subtitle2">
                              <strong>Latitude: </strong> {address["latitude"]}
                            </Typography>
                            <Typography variant="subtitle2">
                              <strong>Longitude: </strong>{" "}
                              {address["longitude"]}
                            </Typography>
                          </CardContent>
                        </Card>
                        <br />
                      </Grid>
                    ))}
                    {NoRecord}
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <BrandLoader open={loading} />
    </div>
  );
};

export default AddressList;
